import { Component, Input, OnChanges, inject } from '@angular/core';
import { ICamara, IDispositivoAlarma, IQueryParam } from 'modelos/src';
import { firstValueFrom } from 'rxjs';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { Lista2Service } from '../../../../auxiliares/servicios/lista2.service';
import { PermisosEntidadesService } from '../../../../auxiliares/servicios/http/permisos-entidades.service';

interface VistaCanal {
  /** El nombre, o "Canal N" si no tiene. */
  titulo: string;
  /** Número (si tiene nombre), habilitación y streams, separados por ·. */
  detalle: string;
  descripcion?: string;
  modelo?: string;
}

interface VistaAlarmaAsociada {
  nombre: string;
  abonado?: string;
  ruta: string[];
}

/** Lo que muestra la pestaña Info, ya armado para la plantilla. */
interface VistaCamara {
  tipo?: string;
  /** P2P: se conecta por número de serie, sin host ni puertos. */
  esP2P: boolean;
  marcaModelo?: string;
  numeroSerie?: string;
  fechaCreacion?: string;
  host?: string;
  puertoHTTP?: string;
  puertoRTSP?: string;
  usuario?: string;
  canales: VistaCanal[];
}

@Component({
  selector: 'app-detalles-camara-info',
  templateUrl: './detalles-camara-info.component.html',
  styleUrl: './detalles-camara-info.component.scss',
  standalone: false,
})
export class DetallesCamaraInfoComponent implements OnChanges {
  helper = inject(HelperService);
  private lista2 = inject(Lista2Service);
  private permisosEntidades = inject(PermisosEntidadesService);

  /** La que ya cargó la página: antes Info la volvía a pedir. */
  @Input() camara?: ICamara;

  public vista?: VistaCamara;
  /** null mientras se piden; solo con el módulo de alarmas. */
  public alarmas: VistaAlarmaAsociada[] | null = null;
  /** Señal: los módulos del permiso pueden llegar después de crear esto. */
  public get verAlarmas(): boolean {
    return this.permisosEntidades.verModuloAlarmas();
  }

  private armarVista(): void {
    const c = this.camara;
    if (!c) {
      this.vista = undefined;
      return;
    }
    const m = c.modeloDispositivo;
    this.vista = {
      tipo: c.tipo,
      esP2P: !!c.tipo?.includes('P2P'),
      marcaModelo: [m?.marca, m?.modelo].filter(Boolean).join(' ') || undefined,
      numeroSerie: c.numeroSerie,
      fechaCreacion: c.fechaCreacion
        ? HelperService.getFechaYHora(c.fechaCreacion)
        : undefined,
      host: c.host,
      puertoHTTP: c.puertoHTTP != null ? String(c.puertoHTTP) : undefined,
      puertoRTSP: c.puertoRTSP != null ? String(c.puertoRTSP) : undefined,
      usuario: c.usuario,
      canales: (c.canales ?? []).map((canal) => {
        const streams = canal.ids?.length ?? 0;
        return {
          titulo: canal.nombre || `Canal ${canal.numero}`,
          detalle: [
            // El número solo si el nombre es otro ("Canal 1" no se repite)
            canal.nombre && canal.nombre !== `Canal ${canal.numero}`
              ? `Canal ${canal.numero}`
              : null,
            canal.tipoHabilitacion === 'Siempre'
              ? 'Siempre habilitado'
              : canal.tipoHabilitacion === 'Con Evento'
                ? 'Con evento'
                : null,
            streams ? `${streams} stream${streams === 1 ? '' : 's'}` : null,
          ]
            .filter(Boolean)
            .join(' · '),
          descripcion: canal.descripcion,
          modelo:
            [canal.modeloDispositivo?.marca, canal.modeloDispositivo?.modelo]
              .filter(Boolean)
              .join(' ') || undefined,
        };
      }),
    };
  }

  /** Las alarmas que tienen esta cámara asociada (idsCamaras). */
  private async listarAlarmas(): Promise<void> {
    const id = this.camara?._id;
    if (!id || !this.verAlarmas) return;
    this.alarmas = null;
    try {
      const query: IQueryParam = {
        filter: JSON.stringify({ idsCamaras: id }),
        select: 'nombre numeroAbonado',
        sort: 'nombre',
        includeChildren: true,
      };
      const data = await firstValueFrom(this.lista2.dispositivosAlarmas(query));
      this.alarmas = (data.datos ?? []).map((a: IDispositivoAlarma) => ({
        nombre: a.nombre || 'Sin nombre',
        abonado: a.numeroAbonado,
        ruta: [
          '/main',
          'alarmas',
          'alarmasDomiciliarias',
          'detallesAlarma',
          a._id,
        ],
      }));
    } catch (error) {
      console.error('Error al listar las alarmas de la cámara', error);
      this.alarmas = [];
    }
  }

  ngOnChanges(): void {
    this.armarVista();
    void this.listarAlarmas();
  }
}
